// Phone/tablet support: media-query hooks and a visual-viewport tracker.
//
// The workspace is sized to window.visualViewport (CSS vars --vvh / --vvt) so the on-screen
// keyboard never covers the terminal. On Android `interactive-widget=resizes-content` (index.html)
// already shrinks the layout viewport; iOS Safari only shrinks the visual viewport, which is what
// this tracker follows.

import { useSyncExternalStore } from 'react';

/**
 * Workspace switches to the phone layout (drawer, single pane, top bar) at or below 768 px wide,
 * and on touch screens that are short (a phone in landscape).
 */
export const COMPACT_MQ = '(max-width: 768px), (pointer: coarse) and (max-height: 500px)';
/** Touch-first device: accessory key bar, tap-to-focus, touch scrolling. */
export const COARSE_MQ = '(pointer: coarse)';
/** Narrow phone screen: smaller terminal font. */
export const NARROW_MQ = '(max-width: 480px)';

const mqs = new Map<string, MediaQueryList>();
function mq(q: string) {
  let m = mqs.get(q);
  if (!m) {
    m = matchMedia(q);
    mqs.set(q, m);
  }
  return m;
}

export const matches = (q: string) => mq(q).matches;

export function useMedia(q: string): boolean {
  return useSyncExternalStore(
    (cb) => {
      const m = mq(q);
      m.addEventListener('change', cb);
      return () => m.removeEventListener('change', cb);
    },
    () => mq(q).matches,
    () => false,
  );
}

// --- visual viewport ------------------------------------------------------------------------

/** Keyboard heuristic: the viewport is this much shorter than the tallest we have seen. */
const KEYBOARD_MIN_PX = 120;

let kbOpen = false;
let baseline = 0;
let baselineWidth = 0;
const kbListeners = new Set<() => void>();
let installed = 0;

function update() {
  const vv = window.visualViewport;
  // Pinch-zoomed: keep the last layout instead of shrinking the app to the zoomed area.
  if (vv && Math.abs(vv.scale - 1) > 0.01) return;
  const h = vv ? vv.height : window.innerHeight;
  const top = vv ? Math.max(0, vv.offsetTop) : 0;
  const w = window.innerWidth;
  if (w !== baselineWidth) {
    // Rotation / window resize: start a new baseline for this width.
    baselineWidth = w;
    baseline = 0;
  }
  baseline = Math.max(baseline, h, vv ? 0 : window.innerHeight);
  const root = document.documentElement.style;
  root.setProperty('--vvh', `${Math.round(h)}px`);
  root.setProperty('--vvt', `${Math.round(top)}px`);
  const open = h < baseline - KEYBOARD_MIN_PX;
  if (open !== kbOpen) {
    kbOpen = open;
    kbListeners.forEach((l) => l());
  }
}

/** Track the visual viewport while the workspace is mounted. Returns an uninstaller. */
export function installViewportTracking(): () => void {
  const vv = window.visualViewport;
  if (installed++ === 0) {
    update();
    vv?.addEventListener('resize', update);
    vv?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
  }
  return () => {
    if (--installed > 0) return;
    vv?.removeEventListener('resize', update);
    vv?.removeEventListener('scroll', update);
    window.removeEventListener('resize', update);
  };
}

export const keyboardOpen = () => kbOpen;

export function useKeyboardOpen(): boolean {
  return useSyncExternalStore(
    (cb) => {
      kbListeners.add(cb);
      return () => kbListeners.delete(cb);
    },
    () => kbOpen,
    () => false,
  );
}
